<template>
    <div class="form__group">
        <div class="dropzone-wrapper">
            <vue-dropzone
                ref="dropzone"
                id="dropzone"
                :options="dropzoneOptions"
                :include-styling="false"
                :destroy-dropzone="true"
                @vdropzone-file-added="onFileAdded"
                @vdropzone-sending="onSend"
                @vdropzone-success="onFileUploaded"
                @vdropzone-error="onFileError"
                @vdropzone-removed-file="onFileRemoved"
                @vdropzone-queue-complete="onComplete"
                @vdropzone-complete="onComplete"
                @vdropzone-processing="onProcessing"
                :class="{ 'error': errors.first(this.name) }"
                :useCustomSlot="true"
            >
                <div class="dropzone-custom-content"
                     v-if="!validFiles.length.length || (validFiles.length.length && multiple)">
                    <span class="subtitle">Drag and drop your files here</span>
                </div>
            </vue-dropzone>
            <div class="selected-files" v-if="files.length">
                <div
                    class="selected"
                    :class="{
                        'is-image' : file.filetype.includes('image/'),
                        'is-valid' : file && file.uuid && file.filename
                    }"
                    v-for="(file, index) in files"
                    :key="file.uuid"
                >
                    <picture class="selected__image" v-if="file && file.filetype.includes('image/')">
                        <img :src="file.url" :alt="file.filename"/>
                    </picture>
                    <span class="selected__filetype" v-else v-text="file.filetype.split('/')[1]"/>
                    <span>{{ file.filename }}</span>
                    <button
                        class="selected__remove"
                        type="button"
                        v-if="file && file.uuid && file.filename"
                        @click.prevent="removeFile(file)"
                    >
                        <SvgIcon icon="x"></SvgIcon>
                    </button>
                </div>
            </div>
        </div>
        <label class="form__label">{{ label }}</label>
        <div class="form__validation">{{ errors.first(this.name) }}</div>
    </div>
</template>

<script>
import vue2Dropzone from 'vue2-dropzone'
import API from '@api';
import SvgIcon from '../SvgIcon';

export default {
    name: 'FileUpload',
    inject: {
        $validator: '$validator',
    },
    $_veeValidate: {
        value() {
            return this.uuids;
        },
        name() {
            return this.name;
        },
    },
    components: {
        SvgIcon,
        vueDropzone: vue2Dropzone,
    },
    props: {
        label: {
            type: String,
            default: 'Drag and drop files here',
        },
        name: {
            type: String,
            required: true,
        },
        value: {
            default: null,
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        fileTypes: {
            default: null,
            required: false,
        },
    },
    data() {
        return {
            files: [],
            uuids: [],
            dropzoneOptions: {
                maxFiles: this.multiple ? 999 : 1,
                url: '/attachments/dropzone',
                createImageThumbnails: true,
                acceptedMimeTypes: this.fileTypes ? this.fileTypes : null,
                previewTemplate: `
                    <div class="dz-preview dz-file-preview">
		                <div class="dz-details">
		                    <div class="dz-filename" data-dz-name></div>
		                    <div class="dz-size" data-dz-size></div>
			                <div class="dz-progress">
			                    <span class="dz-upload" data-dz-uploadprogress></span>
		                    </div>
		                </div>
		                <div class="dz-error-message"><span data-dz-errormessage></span></div>
		            </div>
                `,
            },
        }
    },
    methods: {
        onFileAdded(file) {
            if(typeof file.manuallyAdded !== "undefined" && file.manuallyAdded){
                this.files.push({
                    filesize: file.size,
                    filename: file.name,
                    filetype: file.type,
                    uuid: file.uuid,
                    status: 'success',
                    url: file.url,
                    manuallyAdded: file.manuallyAdded,
                    group: 'dropzone_preview'
                });
                this.uuids.push(file.uuid);
            }
            this.$refs.dropzone.dropzone._updateMaxFilesReachedClass();
        },
        onSend(file, xhr, formData) {
            xhr.setRequestHeader('Authorization', `Bearer ${ this.$store.state.auth.token }`);
            formData.append('group', 'dropzone_preview');
        },
        onFileUploaded(file, response) {
            const formattedFile = {
                ...response,
                dropzoneFile: file,
            };

            // This was technically never working correct with dropzone as the file that dropzone had saved was a different object.
            // So removing was actually never removing them from the actual dropzone instance.
            // This meant I couldn't use `dz-max-files-reached`.
            // Attached dropzone file to the files object to use later on to remove from the actual dropzone instance.
            this.files.push(formattedFile);
            this.uuids.push(response.uuid);
        },
        onFileRemoved(removed, error, xhr) {
            // There is a race condition failing with options.maxFiles updating back to the correct amount in the dropzone instance...
            // So wrapping this is a setTimout Fixes it
            setTimeout(() => { this.$refs.dropzone.dropzone._updateMaxFilesReachedClass() }, 0);
        },
        onFileError(files, message, xhr) {
            console.log('Failed with error');
        },
        onComplete() {
            this.$wait.end(`${ this.name } uploading`);
        },
        onProcessing() {
            this.$wait.start(`${ this.name } uploading`);
        },
        updateFromValue() {
            if(this.multiple) {
                this.uuids = Array.isArray(this.value) ? this.value : [];

                if(this.uuids.length) {
                    API.getAttachments(this.uuids)
                        .then((resp) => {
                            resp.forEach(this.addFileToDropzone.bind(this));
                        });
                }
            } else if(this.value) {
                API.getAttachment(this.value)
                    .then(this.addFileToDropzone.bind(this))
            }
        },
        addFileToDropzone(attachment) {
            try {
                this.$refs.dropzone.manuallyAddFile(
                    {
                        size: attachment.filesize,
                        name: attachment.filename,
                        type: attachment.filetype,
                        uuid: attachment.uuid,
                        key: attachment.key,
                        url: attachment.url,
                        metadata: attachment.metadata,
                        manuallyAdded: true,
                    },
                    attachment.url,
                );
                this.$refs.dropzone.dropzone._updateMaxFilesReachedClass();
            } catch(err) {
                console.warn(err)
            }
        },
        removeFile(file) {
            if(typeof file.manuallyAdded !== "undefined" && file.manuallyAdded) {
                const dzFiles = this.$refs.dropzone.getFilesWithStatus();
                const selectedFile = dzFiles.find((dzFile) => dzFile.uuid === file.uuid);
                this.$refs.dropzone.removeFile(selectedFile);
            } else {
                const dzFiles = this.$refs.dropzone.getAcceptedFiles();
                const selectedFile = dzFiles.find((dzFile) => dzFile.uuid === file.dropzoneFile.uuid);
                this.$refs.dropzone.removeFile(selectedFile);
            }

            this.files = this.files.filter(innerFile => innerFile.uuid !== file.uuid);
            this.uuids = this.uuids.filter((innerUUID) => innerUUID !== file.uuid);

            this.$refs.dropzone.dropzone._updateMaxFilesReachedClass();

            API.deleteAttachment(file.uuid)
                .then((e) => console.log(e))
                .catch((err) => console.warn(err));
        },
        clearAll() {
            this.$refs.dropzone.removeAllFiles(true);
            this.files = [];
            this.uuids = [];
        }
    },
    mounted() {
        this.updateFromValue();
    },
    computed: {
        validFiles() {
            return this.files.filter(file => file && file.uuid && file.filename);
        },
    },
    watch: {
        uuids(files) {
            this.$emit('input', this.multiple ? files : files[0] || null);
        },
        value() {
            if(!this.files.length) {
                this.updateFromValue();
            }
        },
    },
}
</script>

<style lang="scss">
@import '~reflex-grid/scss/includes/variables';
@import '~reflex-grid/scss/includes/mixins';
@import '~@/theme/_variables.scss';
@import '~@/mixins/_class.scss';
@import '~@/theme/_extenables.scss';

.dropzone-wrapper {
    border: 0.1rem solid $secondary;
    border-radius: 0.4rem;
    background-color: #FFFFFF;
    padding: 2rem;
    order: 2;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    row-gap: 2rem;
    min-height: 5.7rem;


    .selected-files {}

    .dz-max-files-reached {
        display: none !important;
    }
}

#dropzone {
    background-color: #FFFFFF;
    cursor: pointer;
    color: $text;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 2rem;
    margin: -2rem;

    &.error {
        border: 0.1rem solid $accent-error;

        + .form__label {
            color: $accent-error;
        }
    }

    .dropzone-custom-content {
        display: flex;
        flex-direction: column;
        align-items: center;

        .subtitle {
            padding-left: 1.6rem;
            display: block;
            margin: 0 auto;
            position: relative;

            &:before {
                content: "";
                position: absolute;
                background-image: url('/images/icons/icon--download.svg');
                height: 1.4rem;
                width: 1.4rem;
                left: 0;
                top: 0;
                background-position: left center;
                background-size: 1.4rem;
                background-repeat: no-repeat;
                color: $text;
                font-size: 1.2rem;
            }
        }
    }
}

.dz-preview {
    margin-top: 1rem;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: stretch;

    &:last-child {
        margin-bottom: 0;
    }

    .dz-details {
        min-width: 2rem;
        margin-bottom: 0.5rem;
        @include display-flex;
        @include align-items(center);

        .dz-filename {
            min-width: 2rem;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
            @include flex(0, 1, auto);
        }

        .dz-size {
            margin-left: 1rem;
            margin-right: auto;
            @include flex(0, 0, auto);

            &:before {
                content: '(';
            }

            &:after {
                content: ')';
            }
        }

        .dz-progress {
            width: 30%;
            margin-left: 2rem;
            height: 0.8rem;
            border: 0.1rem solid $secondary;
            border-radius: 0.4rem;
            @include flex(0, 0, auto);
            @include display-flex;
            @include align-items(stretch);

            .dz-upload {
                background-color: $accent;
            }
        }
    }

    .dz-error-message {
        color: $accent-error;
    }

    &.dz-complete {
        display: none;
    }

    &.dz-error {
        .dz-progress .dz-upload {
            background-color: $accent-error;
        }
    }
}

.selected {
    font-size: 1.2rem;
    line-height: 1.4rem;
    color: $link;
    display: flex;
    align-items: center;
    padding: 0;
    column-gap: 1rem;

    .selected__filetype {
        height: 3.5rem;
        width: 3.5rem;
        overflow: hidden;
        border-radius: .4rem;
        font-size: 1rem;
        letter-spacing: -0.05rem;
        line-height: 1.2rem;
        text-transform: uppercase;
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: $secondary;
        font-weight: 600;
    }

    .selected__image {
        height: 3.5rem;
        width: 3.5rem;
        overflow: hidden;
        border-radius: .4rem;

        img {
            height: 100%;
            width: 100%;
            object-fit: cover;
        }
    }

    .selected__remove {
        appearance: none;
        -webkit-appearance: none;
        border: none;
        background: transparent;
        border-radius: 0;
        box-shadow: none;
        outline: none;
        padding: 0.5rem;
        margin-left: 0.5rem;
        cursor: pointer;
        color: $text;

        .svg-icon {
            width: 1.5rem;
            height: 1.5rem;
        }
    }
}

.selected-files {
    order: 3;

    .selected + .selected {
        margin-top: 1rem;
    }
}
</style>
